import { Container, Flex, Grid, Image, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getBookAuthors, getBookById } from '@/entities/books/api/books-api';
import { Book } from '@/entities/books/model/types';
import { getPublishers } from '@/entities/publisher/api/publisher-api';
import { BookLessonsList } from '@/pages';
import { getCategories } from '@/shared/api/categories';
import {
  useCurrentProject,
  useProjectPath,
} from '@/shared/projects/project-path';
import { Button } from '@/shared/ui';

import styles from './book-description.module.scss';
import { KitabaBookDescription } from './kitaba-book-description';

const initialBookInfo: Book = {
  id: -1,
  title: '',
  categoryIds: [],
  authorId: 0,
  translatorName: '',
  readerName: '',
  description: '',
  isPublished: false,
  imgUrl: '',
  circleImgUrl: '',
  imagePath: '',
  circleImagePath: '',
  publisherId: 0,
};

export const BookDescription = () => {
  const project = useCurrentProject();
  return project === 'kitaba' ? (
    <KitabaBookDescription />
  ) : (
    <HatfaBookDescription />
  );
};

const HatfaBookDescription = () => {
  const [bookInfo, setBookInfo] = useState<Book>(initialBookInfo);
  const [authorName, setAuthorName] = useState('');
  const [publishersName, setPublishersName] = useState('');
  const [categoriesName, setCategoriesName] = useState('');
  const { bookId } = useParams();
  const navigate = useNavigate();
  const project = useCurrentProject();
  const withProject = useProjectPath();

  const fetchBookCategories = async () => {
    try {
      if (bookInfo.id) {
        const { categories } = await getCategories(project);

        if (categories) {
          const foundCategories = categories
            .filter((category) => bookInfo.categoryIds.includes(category.id))
            .map((category) => category.title)
            .join(', ');
          setCategoriesName(foundCategories);
        }
      }
    } catch (error) {
      console.error('Error loading data', error);
    }
  };

  const fetchBookAuthors = async () => {
    try {
      if (bookInfo.id) {
        const { authors } = await getBookAuthors();

        if (authors) {
          const foundAuthor = authors.find(
            (author) => author.id === bookInfo.authorId,
          );
          if (foundAuthor) {
            setAuthorName(foundAuthor.name);
          }
        }
      }
    } catch (error) {
      console.error('Error loading data', error);
    }
  };

  const fetchBookPublishersData = async () => {
    try {
      if (bookInfo.id) {
        const { publishers } = await getPublishers();

        if (publishers) {
          const foundPublisher = publishers.find(
            (publisher) => publisher.id === bookInfo.publisherId,
          );
          if (foundPublisher) {
            setPublishersName(foundPublisher.title);
          }
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке списка издательств', error);
    }
  };

  const fetchBookData = async () => {
    try {
      if (bookId) {
        const bookResponse = await getBookById(bookId);
        setBookInfo(bookResponse);
      }
    } catch (error) {
      console.error('Ошибка при загрузке книги', error);
    }
  };

  useEffect(() => {
    fetchBookData();
  }, [bookId]);

  useEffect(() => {
    if (bookInfo.id !== -1) {
      fetchBookAuthors();
      fetchBookPublishersData();
      fetchBookCategories();
    }
  }, [bookInfo.id]);

  const bookDetails = [
    {
      label: 'Категория',
      value: categoriesName,
    },
    {
      label: 'Издательство',
      value: publishersName,
    },
    {
      label: 'Автор',
      value: authorName,
    },
    {
      label: 'Переводчик',
      value: bookInfo.translatorName,
    },
    {
      label: 'Диктор',
      value: bookInfo.readerName,
    },
  ].map(({ label, value }) => ({
    label,
    value: value || 'Не выбрано',
  }));

  return (
    <Flex direction="column" className={styles.wrapper}>
      <Flex className={styles.container}>
        <Flex className={styles.leftColumn}>
          <Title className={styles.title}>{bookInfo.title}</Title>
          <Text className={styles.description}>{bookInfo.description}</Text>

          <Container className={styles.detailsContainer}>
            {bookDetails.map(({ label, value }) => (
              <Grid key={label} className={styles.detailsRow}>
                <Grid.Col span={5}>
                  <Text className={styles.label}>{label}</Text>
                </Grid.Col>
                <Grid.Col span={7}>
                  <Text className={styles.value}>{value}</Text>
                </Grid.Col>
              </Grid>
            ))}
          </Container>

          <Button
            className={styles.editButton}
            onClick={() =>
              navigate(withProject(`/books/${String(bookInfo.id)}`))
            }
          >
            Редактировать книгу
          </Button>
        </Flex>

        <Flex className={styles.rightColumn}>
          <Flex
            className={`${styles.status} ${
              bookInfo.isPublished ? styles.published : styles.draft
            }`}
          >
            {bookInfo.isPublished ? 'Опубликовано' : 'Черновик'}
          </Flex>

          <Flex className={styles.images}>
            {bookInfo.circleImgUrl && (
              <Image
                src={bookInfo.circleImgUrl}
                alt="Основное изображение"
                className={styles.image}
              />
            )}

            {bookInfo.imgUrl && (
              <Image
                src={bookInfo.imgUrl}
                alt="Основное изображение"
                className={styles.image}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      <BookLessonsList />
    </Flex>
  );
};
